import { useEffect, useState } from "react";
import {
  CircleNotch, FilePdf, FileImage, FileVideo, FileDoc, FileXls, File as FileIcon,
  DownloadSimple, Files, MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { repApi, REP_TOKEN_KEY } from "@/lib/repApi";

const KIND_ICON = {
  pdf:   FilePdf,
  image: FileImage,
  video: FileVideo,
  doc:   FileDoc,
  sheet: FileXls,
  text:  FileDoc,
  other: FileIcon,
};

const KIND_LABEL = {
  pdf: "PDF",
  image: "Imagem",
  video: "Vídeo",
  doc: "Documento",
  sheet: "Planilha",
  text: "Texto",
  other: "Arquivo",
};

const KIND_TONE = {
  pdf:   "text-rose-300",
  image: "text-emerald-300",
  video: "text-purple-300",
  doc:   "text-blue-300",
  sheet: "text-amber-300",
  text:  "text-slate-300",
  other: "text-slate-300",
};

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

// v0.96.19 — Monta URL autenticada por query param `?auth=<token>`.
// Motivo: iOS Safari ignora o header `Authorization` em <a download>
// links (o gesto do usuário precisa de URL "pura"). Antes usávamos
// blob + window.open, mas iOS Safari BLOQUEIA `window.open(blobUrl)`
// silenciosamente sem gesto do usuário → download nunca acontecia.
// Backend aceita esse query param via `current_rep_account_download`.
function buildAuthedDownloadUrl(id) {
  const token = localStorage.getItem(REP_TOKEN_KEY);
  const base = repApi.defaults.baseURL || "";
  const url = `${base}/rep/materials/${id}/download`;
  return token ? `${url}?auth=${encodeURIComponent(token)}` : url;
}

async function downloadOrOpen(id, filename, contentType) {
  // v0.96.19 — Estratégia iOS-safe: navega direto pra URL autenticada
  // por query param. O browser cuida do download/preview conforme o
  // Content-Disposition retornado pelo backend (inline pra PDF/imagem,
  // attachment implícito pra outros). Funciona em iOS Safari, Android,
  // desktop — sem depender de blob URL nem gesto adicional.
  try {
    const url = buildAuthedDownloadUrl(id);
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    // `download` como hint. iOS ignora, mas os outros browsers usam
    // pra escolher o nome do arquivo salvo.
    if (filename) a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch {
    toast.error("Falha ao baixar o material.");
  }
}

export default function RepMaterials() {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    repApi.get("/rep/materials").then(({ data }) => setItems(data));
  }, []);

  const filtered = (items || []).filter((m) => {
    if (filter && m.file_kind !== filter) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (m.title || "").toLowerCase().includes(q) ||
           (m.description || "").toLowerCase().includes(q);
  });

  const kindsPresent = Array.from(new Set((items || []).map((m) => m.file_kind))).sort();

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1200px] mx-auto" data-testid="rep-materials-page">
      <header className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
          Portal do Representante
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-white mt-1">
          Materiais
        </h1>
        <p className="text-sm text-slate-400 mt-1.5">
          Argumentos de vendas, apresentações, vídeos e tabelas
          disponibilizados pela equipe AgentUS. {items === null ? "Carregando…" : (
            <>{items.length} {items.length === 1 ? "material" : "materiais"} disponíveis.</>
          )}
        </p>
      </header>

      {items !== null && items.length > 0 && (
        <>
          <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3 flex items-center gap-2 mb-3">
            <MagnifyingGlass size={14} weight="regular" className="text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por título ou descrição…"
              data-testid="rep-materials-search"
              className="bg-transparent flex-1 text-sm text-white placeholder:text-slate-600 focus:outline-none"
            />
          </div>

          <div className="flex items-center gap-1.5 mb-4 flex-wrap">
            <button
              onClick={() => setFilter("")}
              data-testid="rep-materials-filter-all"
              className={`text-[12px] px-3 py-1.5 rounded-md transition-colors ${
                filter === ""
                  ? "bg-white/10 text-white"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
            >
              Todos
            </button>
            {kindsPresent.map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`rep-materials-filter-${k}`}
                className={`text-[12px] px-3 py-1.5 rounded-md transition-colors ${
                  filter === k
                    ? "bg-white/10 text-white"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
              >
                {KIND_LABEL[k] || k}
              </button>
            ))}
          </div>
        </>
      )}

      {items === null ? (
        <div className="py-16 text-center">
          <CircleNotch size={20} weight="regular" className="text-slate-500 animate-spin inline-block" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white/[0.03] border border-white/10 rounded-lg py-16 text-center text-slate-500" data-testid="rep-materials-empty">
          <Files size={24} weight="regular" className="inline-block mb-3 text-slate-600" />
          <div className="text-sm">Nenhum material disponível ainda.</div>
          <div className="text-[12px] mt-1">
            Assim que a equipe AgentUS publicar materiais, eles aparecerão aqui.
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white/[0.03] border border-white/10 rounded-lg py-16 text-center text-slate-500">
          <div className="text-sm">Nenhum material encontrado com esse filtro.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((m) => {
            const Icon = KIND_ICON[m.file_kind] || FileIcon;
            const tone = KIND_TONE[m.file_kind] || "text-slate-300";
            // v0.96.19 — Card virou <a> real com href pra download/preview.
            // Motivo: iOS Safari só respeita o gesto do usuário quando é
            // navegação direta via <a href>, não com div+onClick+programmatic.
            const href = buildAuthedDownloadUrl(m.id);
            return (
              <a
                key={m.id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                download={m.original_filename || undefined}
                className="block bg-white/[0.03] border border-white/10 rounded-lg p-4 hover:bg-white/[0.06] hover:border-white/20 transition-colors cursor-pointer group no-underline"
                data-testid={`rep-material-${m.id}`}
              >
                <div className="flex items-start gap-3">
                  <Icon size={32} weight="regular" className={`${tone} mt-0.5 shrink-0`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-medium leading-snug">{m.title}</div>
                    {m.description && (
                      <div className="text-[12px] text-slate-400 mt-1 line-clamp-3">{m.description}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                  <div className="text-[11px] text-slate-500">
                    {KIND_LABEL[m.file_kind] || "Arquivo"} · {fmtSize(m.size_bytes)} · {fmtDate(m.created_at)}
                  </div>
                  <span className="text-[11px] text-slate-400 group-hover:text-white inline-flex items-center gap-1 transition-colors">
                    <DownloadSimple size={12} weight="regular" /> Abrir
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
